import { withTiming } from '@common/core/utils/withTiming'
import { useWorkspacesStore } from '@common/modules/workspaces/stores/workspaces.store'
import { getLastUsedProjectIdForWorkspace } from '@common/modules/workspaces/utils/getLastUsedProjectIdForWorkspace'

import {
  addProject as _addProject,
  deleteProject as _deleteProject,
  getProjectsByUserId as fetchProjectsViaUserSession,
  updateProjectDescription as _updateProjectDescription,
  updateProjectLogo as _updateProjectLogo,
  updateProjectName as _updateProjectName,
} from '../projects'
import type { AddProjectPayload } from '../types'
import type { Project } from '../types/project.types'
import { resolveProjectForWorkspace } from '../utils/resolveProjectForWorkspace'
import { resolveProjectsStateAfterWorkspaceFetch } from '../utils/resolveProjectsStateAfterWorkspaceFetch'

import { useProjectsStore } from './projects.store'
import { getProjectsByUserIdDeduped, resetProjectsFetchDedupedInFlight } from './utils/getProjectsByUserIdDeduped'

const runGetProjectsByUserIdWithTiming = withTiming('getProjectsByUserId', fetchProjectsViaUserSession)

/** Shared promise while one load is in flight (parallel init / Strict Mode won’t duplicate the API). */
let getProjectsByUserIdInFlight: Promise<Project[]> | null = null

export function resetProjectsFetchInFlight(): void {
  getProjectsByUserIdInFlight = null
  resetProjectsFetchDedupedInFlight()
}

async function loadUserProjectsIntoStoreIfNeeded(): Promise<Project[]> {
  const { setState, userProjectsFetched, allProjects } = useProjectsStore.getState()
  if (userProjectsFetched) {
    return allProjects
  }

  if (getProjectsByUserIdInFlight === null) {
    getProjectsByUserIdInFlight = runGetProjectsByUserIdWithTiming().finally(() => {
      getProjectsByUserIdInFlight = null
    })
  }

  const all = await getProjectsByUserIdInFlight
  setState({ allProjects: all, userProjectsFetched: true })
  return all
}

export const ensureAllUserProjectsFetched = async (): Promise<void> => {
  try {
    await loadUserProjectsIntoStoreIfNeeded()
  } catch (err) {
    console.error('[ensureAllUserProjectsFetched]', err)
  }
}

export const getProjectsByUserId = async (workspaceId?: string): Promise<void> => {
  const { setState } = useProjectsStore.getState()
  setState({ loading: true, error: null })

  const workspace = useWorkspacesStore.getState().currentWorkspace
  const targetWorkspaceId = workspaceId ?? workspace?.id

  if (!targetWorkspaceId) {
    setState({ projects: [], loading: false })
    return
  }

  try {
    const state = useProjectsStore.getState()
    let all = state.allProjects
    if (!state.userProjectsFetched) {
      all = await getProjectsByUserIdDeduped()
      setState({ allProjects: all, userProjectsFetched: true })
    }
    const { projects, currentProject } = resolveProjectsStateAfterWorkspaceFetch(
      all,
      targetWorkspaceId,
      state.currentProject
    )

    setState({
      projects,
      currentProject,
    })
  } catch (err) {
    setState({ projects: [], allProjects: [], userProjectsFetched: false })
    throw err
  } finally {
    setState({ loading: false })
  }
}

export async function getCurrentProject(workspaceId?: string): Promise<Project | null> {
  const { setState } = useProjectsStore.getState()

  const { currentWorkspace, workspaces } = useWorkspacesStore.getState()
  const targetWorkspaceId = workspaceId ?? currentWorkspace?.id

  if (!targetWorkspaceId) {
    return null
  }

  const lastUsedProjectId = getLastUsedProjectIdForWorkspace(workspaces, currentWorkspace, targetWorkspaceId)
  const { projects: slice } = useProjectsStore.getState()

  const fromSlice = resolveProjectForWorkspace(slice, targetWorkspaceId, lastUsedProjectId)
  if (fromSlice) {
    setState({ currentProject: fromSlice })
    return fromSlice
  }

  await getProjectsByUserId(targetWorkspaceId)
  const after = useProjectsStore.getState()
  const resolved =
    resolveProjectForWorkspace(after.projects, targetWorkspaceId, lastUsedProjectId) ??
    resolveProjectForWorkspace(after.allProjects, targetWorkspaceId, lastUsedProjectId) ??
    after.currentProject ??
    null
  if (resolved) {
    setState({ currentProject: resolved })
  }
  return resolved
}

export const addProject = withTiming(
  'addProject',
  async (projectData: AddProjectPayload): Promise<string | null | void> => {
    const { setState, invalidateProjectsListCache } = useProjectsStore.getState()
    try {
      setState({ loading: true, error: null })
      const id = await _addProject(projectData)
      invalidateProjectsListCache()
      resetProjectsFetchInFlight()
      return id
    } finally {
      setState({ loading: false })
    }
  }
)

export const deleteProject = withTiming('deleteProject', async (projectId: string): Promise<void> => {
  const { setState, invalidateProjectsListCache } = useProjectsStore.getState()
  try {
    setState({ loading: true, error: null })
    await _deleteProject(projectId)
    invalidateProjectsListCache()
    resetProjectsFetchInFlight()
  } finally {
    setState({ loading: false })
  }
})

export const updateProjectName = withTiming(
  'updateProjectName',
  async (projectId: string, newName: string): Promise<void> => {
    const { setState, setProjects, setCurrentProject } = useProjectsStore.getState()
    try {
      setState({ loading: true, error: null })
      await _updateProjectName(projectId, newName)
      setProjects(useProjectsStore.getState().projects.map((p) => (p.id === projectId ? { ...p, name: newName } : p)))
      const current = useProjectsStore.getState().currentProject
      if (current?.id === projectId) {
        setCurrentProject({ ...current, name: newName })
      }
    } finally {
      setState({ loading: false })
    }
  }
)

export const updateProjectDescription = withTiming(
  'updateProjectDescription',
  async (projectId: string, newDescription: string): Promise<void> => {
    const { setState, setProjects, setCurrentProject } = useProjectsStore.getState()
    try {
      setState({ loading: true, error: null })
      await _updateProjectDescription(projectId, newDescription)
      setProjects(
        useProjectsStore
          .getState()
          .projects.map((p) => (p.id === projectId ? { ...p, description: newDescription || '' } : p))
      )
      const current = useProjectsStore.getState().currentProject
      if (current?.id === projectId) {
        setCurrentProject({ ...current, description: newDescription || '' })
      }
    } finally {
      setState({ loading: false })
    }
  }
)

export const updateProjectLogo = withTiming(
  'updateProjectLogo',
  async (projectId: string, newLogo: number[]): Promise<void> => {
    const { setState } = useProjectsStore.getState()
    try {
      setState({ loading: true, error: null })
      await _updateProjectLogo(projectId, newLogo)
    } finally {
      setState({ loading: false })
    }
  }
)
